/* MyLove v9 — vanilla JS (no GSAP/Lenis for performance).
   Reproduces v9 mockup behaviors: marquee, header hide, scroll progress,
   lazy videos, counters, FAQ, find tabs, gender toggle, smooth scroll, reveals. */
(function () {
  'use strict';
  if (window.__mylvBaseLoaded) return;
  window.__mylvBaseLoaded = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function whenReady(cb) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cb, { once: true });
    } else {
      cb();
    }
  }

  /* ─── Reveal on scroll ─── */
  function initReveals() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.mylv [data-reveal]').forEach(el => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const delay = parseFloat(el.getAttribute('data-reveal-delay') || 0);
          if (delay) el.style.transitionDelay = delay + 's';
          el.classList.add('is-visible');
          io.unobserve(el);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    document.querySelectorAll('.mylv [data-reveal]').forEach(el => io.observe(el));
  }

  /* ─── Seamless infinite marquee ─── */
  function initMarquees() {
    const tracks = document.querySelectorAll('.mylv [data-marquee]');
    tracks.forEach(track => {
      if (track.dataset.cloned) return;
      const speed = parseFloat(track.dataset.marqueeSpeed) || 50;
      const items = Array.from(track.children);
      items.forEach(it => {
        const c = it.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        track.appendChild(c);
      });
      track.dataset.cloned = '1';
      const setup = () => {
        const w = track.scrollWidth / 2;
        const dur = Math.max(20, w / speed);
        track.style.setProperty('--marquee-duration', dur + 's');
        track.classList.add('is-running');
      };
      setup();
      window.addEventListener('resize', setup, { passive: true });
    });
  }

  /* ─── Header hide on scroll ─── */
  function initHeader() {
    const header = document.querySelector('.mylv .header, .mylv-header');
    if (!header) return;
    let lastY = 0;
    let ticking = false;
    function update() {
      const y = window.scrollY;
      if (y > 100 && y > lastY) header.classList.add('hidden');
      else header.classList.remove('hidden');
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
  }

  /* ─── Scroll progress bar ─── */
  function initScrollProgress() {
    const bar = document.querySelector('.mylv .scroll-progress');
    if (!bar) return;
    let ticking = false;
    function update() {
      const h = document.documentElement;
      const scrolled = h.scrollTop / Math.max(1, h.scrollHeight - h.clientHeight);
      bar.style.width = (scrolled * 100) + '%';
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ─── Lazy videos (autoplay when in viewport) ─── */
  function initLazyVideos() {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const v = entry.target;
        if (entry.isIntersecting) {
          const src = v.querySelector('source');
          if (src && !src.src && src.dataset.src) {
            src.src = src.dataset.src;
            v.load();
          }
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.25 });
    document.querySelectorAll('.mylv video[data-lazy-video], .mylv video[data-lazy]').forEach(v => io.observe(v));
  }

  /* ─── Counters ─── */
  function initCounters() {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = parseFloat(el.getAttribute('data-counter')) || 0;
        const decimals = parseInt(el.getAttribute('data-counter-decimals') || 0, 10);
        const suffix = el.getAttribute('data-counter-suffix') || '';
        const dur = 1500;
        const start = performance.now();
        function tick(now) {
          const t = Math.min(1, (now - start) / dur);
          const eased = 1 - Math.pow(1 - t, 3);
          const value = target * eased;
          el.textContent = (decimals ? value.toFixed(decimals) : Math.round(value).toLocaleString()) + suffix;
          if (t < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
        io.unobserve(el);
      });
    }, { threshold: 0.4 });
    document.querySelectorAll('.mylv [data-counter]').forEach(el => io.observe(el));
  }

  /* ─── FAQ accordion ─── */
  function initFAQ() {
    document.querySelectorAll('.mylv .faq-item .faq-q').forEach(q => {
      q.addEventListener('click', () => {
        const item = q.closest('.faq-item');
        const wasOpen = item.classList.contains('open');
        const list = item.closest('.faq-list');
        if (list) list.querySelectorAll('.faq-item.open').forEach(s => s.classList.remove('open'));
        if (!wasOpen) item.classList.add('open');
      });
    });
  }

  /* ─── Find tabs ─── */
  function initFindTabs() {
    document.querySelectorAll('.mylv .find-tabs').forEach(group => {
      const root = group.closest('.find') || group.parentElement;
      const tabs = group.querySelectorAll('.find-tab');
      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          tabs.forEach(t => t.classList.remove('active'));
          tab.classList.add('active');
          const target = tab.dataset.find;
          root.querySelectorAll('.find-grid').forEach(g => {
            g.style.display = g.id === 'find-' + target ? '' : 'none';
          });
        });
      });
    });
  }

  /* ─── Gender toggle ─── */
  function initGenderToggle() {
    document.querySelectorAll('.mylv .gender-toggle').forEach(group => {
      const section = group.closest('.bestsellers') || group.closest('.mylv');
      const btns = group.querySelectorAll('.gender-btn');
      btns.forEach(btn => {
        btn.addEventListener('click', () => {
          btns.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          if (section) section.classList.toggle('men-mode', btn.dataset.gender === 'men');
        });
      });
    });
  }

  /* ─── Smooth-scroll links ─── */
  function smoothScrollTo(target, offset = 80) {
    const node = document.querySelector(target);
    if (!node) return;
    const top = node.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' });
  }
  function initScrollLinks() {
    document.querySelectorAll('.mylv [data-scroll-to]').forEach(el => {
      el.addEventListener('click', (e) => {
        const sel = el.getAttribute('data-scroll-to');
        if (!sel) return;
        e.preventDefault();
        smoothScrollTo(sel, 80);
      });
    });
  }

  whenReady(() => {
    initReveals();
    initMarquees();
    initHeader();
    initScrollProgress();
    initLazyVideos();
    initCounters();
    initFAQ();
    initFindTabs();
    initGenderToggle();
    initScrollLinks();
  });
})();
